import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { AlertOctagon, OctagonX, Recycle } from 'lucide-react';
import { RESULT_CATEGORIES, type BatchDetail, type ExecutionItemState, type ExecutionRunSummary, type ResultCategory } from '@autopilot/contracts';
import { api } from '../../api/client';
import { qk, useRoles } from '../../api/hooks';
import { useBackoffInterval } from '../../lib/polling';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { KpiCard } from '../../components/KpiCard';
import { ErrorAlert } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { TokensPerResolved, TokenValue } from '../../components/TokenValue';
import { CLOSE_REASON_LABEL, RESULT_CATEGORY_LABEL } from '../../lib/labels';
import { formatDateTime, formatDuration } from '../../lib/format';
import { formatInt } from '../../lib/tokens';
import type { TableFilter } from './TicketTable';

const TERMINAL: ExecutionItemState[] = ['SUCCEEDED', 'CONFLICT', 'FAILED', 'UNKNOWN', 'SENT_NOT_RESOLVED', 'CANCELLED'];

function n(counts: ExecutionRunSummary['counts'], ...keys: ExecutionItemState[]) {
  return keys.reduce((a, k) => a + (counts[k] ?? 0), 0);
}

export function ExecutionPanel({ batch, run: initial }: { batch: BatchDetail; run: ExecutionRunSummary }) {
  const qc = useQueryClient();
  const { isOperator } = useRoles();
  const [confirm, setConfirm] = useState(false);
  const running = initial.state === 'RUNNING' || initial.state === 'STOPPING';
  const interval = useBackoffInterval(running);
  const q = useQuery({
    queryKey: qk.run(initial.id),
    queryFn: () => api.getExecutionRun(initial.id),
    initialData: initial,
    refetchInterval: interval,
    refetchIntervalInBackground: true,
  });
  const run = q.data ?? initial;
  const stop = useMutation({
    mutationFn: () => api.stopExecutionRun(run.id),
    onSuccess: (r) => {
      qc.setQueryData(qk.run(run.id), r);
      void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
      setConfirm(false);
    },
  });
  const c = run.counts;
  const total = Object.values(c).reduce((a, b) => a + (b ?? 0), 0);
  const done = n(c, ...TERMINAL);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const unknown = n(c, 'UNKNOWN');
  const isRunning = run.state === 'RUNNING' || run.state === 'STOPPING';

  const tiles: { label: string; value: number; tone?: 'error' | 'warning' | 'success' }[] = [
    { label: 'Wartet auf Übergabe', value: n(c, 'QUEUED') },
    { label: 'Übergeben', value: n(c, 'SUBMITTING') },
    { label: 'Versand ausstehend', value: n(c, 'PENDING_DELIVERY') },
    { label: 'Erfolgreich gelöst', value: n(c, 'SUCCEEDED'), tone: 'success' },
    { label: 'Konflikte', value: n(c, 'CONFLICT'), tone: 'warning' },
    { label: 'Fehler', value: n(c, 'FAILED'), tone: 'error' },
    { label: 'Versand ungeklärt', value: unknown, tone: 'error' },
    { label: 'Versendet, nicht gelöst', value: n(c, 'SENT_NOT_RESOLVED'), tone: 'warning' },
    { label: 'Nicht mehr begonnen', value: n(c, 'CANCELLED') },
  ];

  return (
    <Card component="section" aria-labelledby={`run-${run.id}`}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography id={`run-${run.id}`} variant="h5" component="h2">
            Ausführung
          </Typography>
          <ToneBadge
            tone={run.state === 'COMPLETED' ? 'success' : run.state === 'NEEDS_ATTENTION' ? 'warning' : run.state === 'CANCELLED' ? 'neutral' : 'info'}
            label={{ RUNNING: 'Läuft', STOPPING: 'Wird gestoppt', COMPLETED: 'Abgeschlossen', NEEDS_ATTENTION: 'Klärung erforderlich', CANCELLED: 'Gestoppt' }[run.state]}
            spinning={isRunning}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: { md: 'auto' } }}>
            {run.authorizationType === 'COMPANY_POLICY' ? `Freigabe: Gesellschaftsregel v${run.policyVersion ?? '?'} (System)` : `Freigegeben von ${run.approver?.name ?? '–'}`} · Schwellen ≥ {run.minCorrectness} / ≤ {run.maxErrorRisk} · Start {formatDateTime(run.createdAt)}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ mt: 2, fontWeight: 600 }} aria-live="polite" data-testid="run-progress">
          {done} von {total} Tickets abgeschlossen
        </Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ my: 1.5, height: 8, borderRadius: 4 }} aria-label={`Ausführung ${pct} Prozent`} />
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
          {tiles.map((t) => (
            <Box
              key={t.label}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: t.tone === 'error' && t.value > 0 ? 'var(--color-danger)' : 'divider',
                backgroundColor: t.tone === 'error' && t.value > 0 ? 'var(--color-danger-soft)' : '#fff',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {t.tone === 'error' && t.value > 0 && <AlertOctagon size={13} aria-hidden />}
                {t.label}
              </Typography>
              <Typography variant="h5" component="p" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {unknown > 0 && (
          <Alert severity="error" icon={<AlertOctagon size={20} />} sx={{ mt: 2 }} data-testid="unknown-delivery">
            <AlertTitle sx={{ fontWeight: 700 }}>Klärungsbedürftig: {unknown} ungeklärte {unknown === 1 ? 'Zustellung' : 'Zustellungen'}</AlertTitle>
            Es ist unbekannt, ob diese Antworten zugestellt wurden. Sie zählen nicht als Erfolg, werden nicht automatisch wiederholt und sind bis zur Klärung für weitere Läufe gesperrt. Bitte den Versandstatus in Nidus prüfen.
          </Alert>
        )}
        {isRunning && isOperator && (
          <Box sx={{ mt: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" color="primary" startIcon={<OctagonX size={18} />} onClick={() => setConfirm(true)} disabled={run.state === 'STOPPING' || stop.isPending} data-testid="stop-execution">
              Weitere Bearbeitung stoppen
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
              Es werden keine weiteren Tickets begonnen. Bereits an Nidus übergebene Antworten können noch versendet und gelöst werden und werden weiter nachverfolgt. Versandte Antworten lassen sich nicht zurücknehmen.
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <ErrorAlert error={stop.error} action="Ausführung stoppen" onClose={() => stop.reset()} />
        </Box>
      </CardContent>
      <ConfirmDialog
        open={confirm}
        title="Weitere Bearbeitung stoppen?"
        confirmLabel="Stoppen"
        onCancel={() => setConfirm(false)}
        onConfirm={() => stop.mutate()}
        busy={stop.isPending}
      >
        Noch nicht begonnene Tickets werden nicht mehr bearbeitet. Bereits übergebene Operationen laufen zu Ende und werden nachverfolgt.
      </ConfirmDialog>
    </Card>
  );
}

export function categoryToTableFilter(c: ResultCategory): TableFilter {
  switch (c) {
    case 'FAILED':
    case 'CONFLICT':
    case 'DELIVERY_UNKNOWN':
    case 'SENT_NOT_RESOLVED':
      return 'failed';
    case 'NEGATIVE_LIST':
    case 'MANUALLY_EXCLUDED':
    case 'CONTEXT_INCOMPLETE':
      return 'excluded';
    case 'BELOW_THRESHOLD':
      return 'blocked';
    default:
      return 'all';
  }
}

export function SummaryPanel({ batch, onShow }: { batch: BatchDetail; onShow: (f: TableFilter, category: ResultCategory) => void }) {
  const results = batch.results;
  const succeeded = results.SUCCEEDED ?? 0;
  const rows = RESULT_CATEGORIES.map((c) => ({ c, count: results[c] ?? 0 })).filter((r) => r.count > 0 || r.c === 'SUCCEEDED');
  const sum = rows.reduce((a, r) => a + r.count, 0);
  return (
    <Box component="section" aria-labelledby="summary-title">
      <Typography id="summary-title" variant="h5" component="h2" sx={{ mb: 1.5 }}>
        Zusammenfassung
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' } }}>
        <KpiCard label="Bestätigte Lösungen" value={formatInt(succeeded)} hint={`von ${formatInt(batch.ticketCount)} Tickets im Batch`} testId="kpi-resolved" />
        <KpiCard label="Gesamttokens" value={<TokenValue usage={batch.usage} />} hint="Generierung, Prüfungen und Wiederholungen dieses Batches" testId="kpi-tokens" />
        <KpiCard
          label="Tokens je gelöstem Ticket"
          value={<TokensPerResolved value={batch.tokensPerResolved} usage={batch.usage} />}
          hint={batch.tokensPerResolved === null ? 'Keine bestätigten Lösungen' : 'Gesamttokens ÷ bestätigte Lösungen'}
          testId="kpi-tokens-per-resolved"
        />
        <KpiCard
          label="Laufzeit"
          value={formatDuration(batch.createdAt, batch.finishedAt)}
          hint={batch.closeReason ? CLOSE_REASON_LABEL[batch.closeReason] : undefined}
        />
      </Box>
      {batch.reusedAnalysisCount > 0 && (
        <Alert severity="info" icon={<Recycle size={18} />} sx={{ mt: 2 }}>
          {batch.reusedAnalysisCount} Analysen wurden aus früheren Batches wiederverwendet ({formatInt(batch.reusedTokens)} Tokens, dort bereits berechnet und hier nicht erneut gezählt).
        </Alert>
      )}
      <Card sx={{ mt: 2 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Ergebnisse nach Kategorie">
            <TableHead>
              <TableRow>
                <TableCell>Ergebnis</TableCell>
                <TableCell align="right">Tickets</TableCell>
                <TableCell>Liste</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ c, count }) => {
                const l = RESULT_CATEGORY_LABEL[c];
                return (
                  <TableRow key={c} data-testid={`result-${c}`}>
                    <TableCell>
                      <ToneBadge tone={l.tone} label={l.label} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {count}
                    </TableCell>
                    <TableCell>
                      {count > 0 && (
                        <Link component="button" onClick={() => onShow(categoryToTableFilter(c), c)}>
                          Tickets anzeigen
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Summe</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {sum}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
}
